<template>
  <section class="mt-4 mb-5">
    <div class="container-fluid">
      <div class="row justify-content-center">
        <div class="col-md-4 text-center">
          <!-- Top bar -->
          <div class="page-bar py-3">
            <div class="container">
              <div class="d-flex align-items-center">
                <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                  <i class="fas fa-chevron-left"></i>
                </BackButton>
                <div class="page-title flex-grow-1">Update Profile</div>
 
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row justify-content-center">
        <div class="col-md-4 mt-3">
          <div class="sticky"> 

            <div class="card shadow-sm">
              <form id="profileForm" @submit.prevent="onSubmitProfile">
                <div class="card-body">
                  <!-- Avatar -->
                  <div class="avatar-wrapper text-center">
                    <div class="avatar-container">
                      <img v-no-right-click :src="avatarPreview || defaultAvatar" alt="Avatar" />
                      <input type="file" id="avatarInput" name="avatar" accept="image/*" hidden @change="onPickAvatar" />
                    </div>
                    <label for="avatarInput" class="edit-btn" :class="{ disabled: isUploadingImage }">
                      <span v-if="isUploadingImage" class="spinner-border spinner-border-sm mr-2"></span>
                      EDIT
                    </label>
                  </div>

                  <!-- About -->
                  <div class="form-group mb-3">
                    <label class="form-control-label">About</label>
                    <div class="custom-field" @click="aboutPicker">
                      {{ about || 'Tap to add a short bio…' }}
                    </div>
                  </div>

                  <!-- Username -->
                  <div class="form-group mb-3">
                    <label class="form-control-label">Username</label>
                    <input type="text" name="username" class="form-control" v-model="username" readonly />
                  </div>

                  <!-- Full Name -->
                  <div class="form-group mb-3">
                    <label class="form-control-label">Full Name</label>
                    <input type="text" name="name" class="form-control" v-model.trim="name" />
                  </div>

                  <!-- Phone -->
                  <div class="form-group mb-3">
                    <label class="form-control-label">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      ref="phoneInput"
                      class="form-control"
                      placeholder="Enter phone number"
                      inputmode="tel"
                      pattern="[0-9]*"
                      maxlength="15"
                      @blur="onPhoneBlur"
                      @input="onPhoneInput"
                    />
                    <input type="hidden" id="pcode" name="phone_code" v-model="phone_code" />
                    <small class="form-text text-muted" v-if="phoneError" style="color: #dc3545 !important;">
                      {{ phoneError }}
                    </small>
                  </div>

                  <!-- Email -->
                  <div class="form-group mb-3">
                    <label class="form-control-label">Email ID</label>
                    <input type="email" name="email" class="form-control" v-model.trim="email" />
                  </div>
                </div>

                <div class="card-footer text-center">
                  <button type="submit" id="btnUpdate" class="btn upload-btn w-50" :disabled="isSavingProfile">
                    <span v-if="isSavingProfile" class="spinner-border spinner-border-sm mr-2"></span>
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- About Modal -->
    <div v-if="showAboutModal" class="modal-backdrop-custom" @click.self="closeAboutModal">
      <div class="modal-card" style="max-width:520px;width:92%">
        <div class="modal-header">
          <h5 class="modal-title">Edit About</h5>
          <button class="close" @click="closeAboutModal">&times;</button>
        </div>
        <div class="modal-body">
          <textarea
            class="form-control"
            rows="6"
            v-model.trim="aboutDraft"
            placeholder="Write a short description about yourself"
          ></textarea>
        </div>
        <div class="modal-footer">
          <button class="btn btn-outline-secondary" @click="closeAboutModal">Cancel</button>
          <button class="btn btn-primary" :disabled="isSavingAbout" @click="saveAbout">
            <span v-if="isSavingAbout" class="spinner-border spinner-border-sm mr-2"></span>
            Save
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useHead } from '@vueuse/head'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'
import Swal from 'sweetalert2'
import intlTelInput from 'intl-tel-input'
import 'intl-tel-input/build/css/intlTelInput.css'
import BackButton from '@/components/common/BackButton.vue'

useHead({
  title: 'Update Profile - Netflyr',
  meta: [{ name: 'description', content: 'Update your profile information' }],
})

const session = useSessionStore()

/* ---------------- fields ---------------- */
const avatarPreview = ref('')
const defaultAvatar = 'https://dummyimage.com/300x300/eee/999.png&text=Avatar'
const username = ref('')
const name = ref('')
const phone = ref('')
const phone_code = ref('')
const email = ref('')
const about = ref('')
const phoneError = ref('')
const intlGlobals = () => (typeof window !== 'undefined' ? window.intlTelInputGlobals : null)
const intlUtils = () => (typeof window !== 'undefined' ? window.intlTelInputUtils : null)
const MIN_PHONE_DIGITS = 9
const MAX_PHONE_DIGITS = 15
const limitDigits = (s = '') => s.replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS)

/* ---------------- modals / loaders ---------------- */
const showAboutModal = ref(false)
const aboutDraft = ref('')
const isSavingAbout = ref(false)
const isSavingProfile = ref(false)
const isUploadingImage = ref(false)

/* ---------------- intl-tel-input ---------------- */
const phoneInput = ref(null)
let iti = null

/* ---------------- helpers ---------------- */
const toast = (msg, icon = 'success') =>
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon,
    title: msg,
    timer: 2200,
    showConfirmButton: false,
  })

const alertErr = (msg) =>
  Swal.fire({ icon: 'error', title: 'Oops…', text: msg })

/* ---------------- phone validation ---------------- */
const validatePhoneNumber = () => {
  extractPhoneData()
  const digits = phone.value
  const len = digits.length
  if (!len || len < MIN_PHONE_DIGITS || len > MAX_PHONE_DIGITS) {
    phoneError.value = `Phone number must be between ${MIN_PHONE_DIGITS} and ${MAX_PHONE_DIGITS} digits`
    return false
  }
  phoneError.value = ''
  return true
}

const onPhoneBlur = () => {
  validatePhoneNumber()
}
const onPhoneInput = () => {
  extractPhoneData()
  if (phoneError.value) phoneError.value = ''
}

/* ---------------- extract phone data ---------------- */
const extractPhoneData = () => {
  if (!iti) return
  const countryData = iti.getSelectedCountryData() || {}
  const rawInput = phoneInput.value?.value || ''
  const fullNumber = iti.getNumber() || rawInput || ''
  const utils = intlUtils()
  let national = ''
  if (utils && typeof utils.numberFormat !== 'undefined') {
    try {
      national = iti.getNumber(utils.numberFormat.NATIONAL) || ''
    } catch {
      national = ''
    }
  }
  if (!national && fullNumber) {
    const dial = countryData.dialCode ? `+${countryData.dialCode}` : ''
    if (dial && fullNumber.startsWith(dial)) {
      national = fullNumber.slice(dial.length)
    } else if (fullNumber.startsWith('+')) {
      national = fullNumber.slice(1)
    } else {
      national = fullNumber
    }
  }
  const digits = limitDigits(rawInput || national || fullNumber)
  phone.value = digits
  phone_code.value = countryData.dialCode ? `+${countryData.dialCode}` : ''
}

const resolveIsoFromDialCode = (dialDigits) => {
  const globals = intlGlobals()
  if (!globals || !dialDigits) return null
  const countryList = typeof globals.getCountryData === 'function' ? globals.getCountryData() : []
  const match = countryList.find((c) => String(c.dialCode) === String(dialDigits))
  return match?.iso2 || null
}

/* ---------------- hydrate ---------------- */
const hydrateProfile = () => {
  const p = session.profile || {}
  if (!Object.keys(p).length) return // No profile yet

  avatarPreview.value = p.user_img || ''
  username.value = p.username || ''
  name.value = p.name || ''
  email.value = p.email || ''
  about.value = p.about || ''

  if (iti) {
    const phoneDigits = String(p.phone || '').replace(/\D/g, '')
    const dialDigits = String(p.phone_code || '').replace(/\D/g, '')
    const iso = resolveIsoFromDialCode(dialDigits)

    if (phoneDigits && dialDigits) {
      iti.setNumber(`+${dialDigits}${phoneDigits}`)
    } else if (p.phone && p.phone.startsWith('+')) {
      iti.setNumber(p.phone)
    } else if (p.phone) {
      iti.setNumber(p.phone)
    } else if (iso) {
      iti.setCountry(iso)
    } else if (dialDigits) {
      iti.setNumber(`+${dialDigits}`)
    }
    extractPhoneData()
  }
}

/* ---------------- avatar upload ---------------- */
const onPickAvatar = async (e) => {
  const file = e.target?.files?.[0]
  if (!file) return
  if (!file.type.match('image.*')) {
    toast('Please choose an image file', 'error')
    e.target.value = ''
    return
  }

  const reader = new FileReader()
  reader.onload = (ev) => (avatarPreview.value = ev.target.result)
  reader.readAsDataURL(file)

  try {
    isUploadingImage.value = true
    const fd = new FormData()
    fd.append('id', session.userId || '')
    fd.append('user_img', file, file.name)
    fd.append('type', 'image')
    const res = await api.updateProfile(fd)
    if (res?.data?.status === 'ok') {
      toast('Profile photo updated')
      session.updateProfileLocally({ user_img: avatarPreview.value })
    } else alertErr(res?.data?.msg || 'Failed to update photo')
  } catch (err) {
    console.error('updateImage failed', err)
    alertErr('Could not upload image.')
  } finally {
    isUploadingImage.value = false
    e.target.value = ''
  }
}

/* ---------------- about modal ---------------- */
const aboutPicker = () => {
  aboutDraft.value = about.value || ''
  showAboutModal.value = true
}
const closeAboutModal = () => (showAboutModal.value = false)

const saveAbout = async () => {
  try {
    isSavingAbout.value = true
    const fd = new FormData()
    fd.append('id', session.userId || '')
    fd.append('description', aboutDraft.value || '')
    fd.append('type', 'description')
    const res = await api.updateProfile(fd)
    if (res?.data?.status === 'ok') {
      about.value = aboutDraft.value
      session.updateProfileLocally({ about: aboutDraft.value })
      showAboutModal.value = false
      toast('Description updated')
    } else alertErr(res?.data?.msg || 'Failed to update description')
  } catch (e) {
    console.error('saveAbout error', e)
    alertErr('Unable to save description.')
  } finally {
    isSavingAbout.value = false
  }
}

/* ---------------- submit ---------------- */
const onSubmitProfile = async () => {
  extractPhoneData()
  if (!validatePhoneNumber()) {
    alertErr('Please enter a valid phone number')
    return
  }

  try {
    isSavingProfile.value = true
    const fd = new FormData()
    fd.append('id', session.userId || '')
    fd.append('username', username.value)
    fd.append('name', name.value)
    fd.append('phone', phone.value)
    fd.append('phone_code', phone_code.value)
    fd.append('email', email.value)
    fd.append('type', 'contact')

    const res = await api.updateProfile(fd)
    if (res?.data?.status === 'ok') {
      toast('Profile updated')
      session.updateProfileLocally({
        name: name.value,
        phone: phone.value,
        phone_code: phone_code.value,
        email: email.value,
      })
    } else alertErr(res?.data?.msg || 'Failed to update profile')
  } catch (e) {
    console.error('updateProfile error', e)
    alertErr('Could not update profile.')
  } finally {
    isSavingProfile.value = false
  }
}

/* ---------------- mount ---------------- */
onMounted(async () => {
  session.loadUser()
  await session.fetchProfile()

  // Initialize intl-tel-input only after DOM + profile ready
  if (phoneInput.value) {
    iti = intlTelInput(phoneInput.value, {
      initialCountry: 'auto',
      fallbackCountry: 'in',
      nationalMode: false,
      separateDialCode: true,
      preferredCountries: ['us', 'gb', 'in', 'ca', 'au'],
      geoIpLookup: async (cb) => {
        try {
          const r = await fetch('https://ipapi.co/json/')
          const d = await r.json()
          cb(d.country_code)
        } catch {
          cb('in')
        }
      },
      utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.1.1/build/js/utils.js',
    })

    phoneInput.value.addEventListener('input', extractPhoneData)
    phoneInput.value.addEventListener('blur', onPhoneBlur)
    phoneInput.value.addEventListener('countrychange', () => {
      extractPhoneData()
      validatePhoneNumber()
    })
  }

  // Wait a tiny delay to ensure iti + utils ready, then hydrate
  setTimeout(() => hydrateProfile(), 500)
})

// If profile updates later (e.g. async), hydrate again
watch(() => session.profile, () => {
  if (session.profile && iti) hydrateProfile()
}, { deep: true, immediate: true })

onUnmounted(() => {
  if (iti) iti.destroy()
})
</script>


<style scoped>
.upload-btn {
  background-color: #fb7210;
  color: #fff;
  padding: 8px 16px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
}

.upload-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.avatar-wrapper {
  text-align: center;
  margin: auto;
  width: 100%;
}

.avatar-container {
  width: 120px;
  height: 120px;
  margin: 0 auto;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid #eee;
}

.avatar-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.edit-btn {
  display: inline-block;
  margin-top: 10px;
  padding: 4px 16px;
  background-color: #0056b3;
  color: #fff;
  font-size: 12px;
  border-radius: 20px;
  cursor: pointer;
}

.edit-btn.disabled {
  pointer-events: none;
  opacity: 0.7;
}

.custom-field {
  border: 1px solid #ccc;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  min-height: 38px;
  display: flex;
  align-items: center;
}

.custom-field:hover {
  background-color: #f9f9f9;
}

.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.modal-header,
.modal-footer {
  padding: 12px 16px;
}

.modal-header {
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-body {
  padding: 16px;
}

.close {
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

/* intl-tel-input adjustments */
.iti {
  width: 100%!important;
  display: block;
}

.iti__flag-container {
  height: 100%;
}

.iti__selected-flag {
  padding: 0 8px;
}

/* Form validation */
.form-text.text-muted {
  display: block;
  margin-top: 4px;
  font-size: 12px;
}
</style>
